import { Divider, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import React, { useContext } from 'react'
import globalStyles from "../../../assets/styles/global.module.scss";
import contactStyle from "../../../assets/styles/componentStyles/campaignContactStyles/campaignContactStyle.module.scss";
import style from "../../../assets/styles/componentStyles/orderDetailStyles/orderDetailStyle.module.scss";
import dashboardStyles from './../../../assets/styles/componentStyles/callAnalysisStyles/callAnalysisDashboard.module.scss';
import { UserContext, UserContextPayload } from '../../../context/userContext';
import { formatDateAccordingToTimezoneUsingMoment, formatString, getValueOrDefault, thousandSeparator } from '../../../utils/constants';
import { useOrderDetailStore } from '../../../store/orderDetailStore';
import { useShallow } from 'zustand/react/shallow';
import { CommonResponseModelWODataArray, orderDetail, orderItem, orderList, orderPass } from '../../../models/axiosModel';
import GlobalButton from '../../utility/globalButton/button';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import agent from '../../../services/api';
import { formatDateToDayMonth } from './orderDetailsUtils';
import { useGenericStore } from '../../../store/genericStore';

const renderOrderInfoField = (label: string, value: React.ReactNode) => (
    <Grid item xs={12} sm={6}>
        <Grid container className={style.orderInfoField} alignItems="center" wrap="nowrap">
            <Grid item className={style.orderInfoLabelGrid}>
                <Typography component="span" className={style.orderInfoLabel}>
                    {label}
                </Typography>
            </Grid>
            <Grid item className={style.orderInfoValueGrid}>
                {typeof value === 'string' || typeof value === 'number' ? (
                    <Typography component="span" className={style.orderInfoValue}>
                        {value}
                    </Typography>
                ) : (
                    value
                )}
            </Grid>
        </Grid>
    </Grid>
);

interface OrderDetailPopupProps {
    renderPaymentStatusBadge: (order: orderList | null, paymentStatus: string) => JSX.Element | string,
    getOrderDetailApiCall: (ecom_order_id: string) => Promise<CommonResponseModelWODataArray<orderDetail>>
} 

const OrderDetailPopup: React.FC<OrderDetailPopupProps> = (props) => {

    const { renderPaymentStatusBadge, getOrderDetailApiCall } = props;
    const { userDetail, setFlagLoading, handleError } = useContext(UserContext) as UserContextPayload

    const innerHeight = (window.innerHeight - 200);

    const { selectedOrderDetail } = useOrderDetailStore(useShallow((state) => ({
        selectedOrderDetail: state.selectedOrderDetail
    })));

    const { showSuccess, showError } = useGenericStore(useShallow((state) => ({
        showSuccess: state.showSuccess,
        showError: state.showError
    })))

    const updatedPassName = (pass: orderPass, orderItem: orderItem) => {
        const date = pass?.validity_date ? formatDateToDayMonth(pass?.validity_date) : orderItem?.product_name;
        const header = pass?.header ?? "";
        return date + " " + header; 
    }

    // Grant Access API
    const grantAccessApi = async (order: orderList, pass: any) => {
        setFlagLoading((prev) => ({...prev, flagRoundLoader: true }));
        const payload = {
            ecom_qr_code_id: pass?.qr_code_id || "",
        }
        const res = await agent.grantAccess.post(payload);
        if(!(res.status)){
            showError(res?.error)
        } else {
            showSuccess("Access Granted Successfully.")
            await getOrderDetailApiCall(order?.order_id);
        }
        setFlagLoading((prev) => ({...prev, flagRoundLoader: false }));
    }
    
    // Resend QR Code API
    const resendQrCode = async (order: orderList) => {
        setFlagLoading((prev) => ({...prev, flagRoundLoader: true }));
        const payload = {
            ecom_order_id: order?.order_id || "",
            ecom_phone_number: order?.phone_no || "",
        }
        const res = await agent.resendQrCode.post(payload);
        if(!(res.status)){
            handleError(res);
        } else {
            showSuccess("Send QR Code Successfully.")
        }
        setFlagLoading((prev) => ({...prev, flagRoundLoader: false }));
    } 

    const checkDisableSendQRCode = (order: orderDetail) => {
        if(order?.payment_status?.toLowerCase() == "paid") return false;
        return true;
    }

    const checkDisableGrantAccess = (pass: any, order: any) => {
        if(order?.payment_status?.toLowerCase() == "paid"){
            if(!(pass?.scanned)) return false;
            return true;
        } 
        return true;
    }

    const renderSendQrCodeBtn = (order: orderDetail | null) => {
        if (!order || order?.order_items?.length === 0) return "-";

        return (
            <GlobalButton
                buttonText="Send"
                startIcon={<QrCode2Icon sx={{ fontSize: '16px !important' }} />}
                sx={{ width: "90px", fontSize: "13px !important" }}
                className='borderedButtonStyle'
                onClick={(e) => {
                    e.stopPropagation();
                    resendQrCode(order);
                }}
                disabled={checkDisableSendQRCode(order)}
            />
        );
    }

    const grantAccessBtn = (order: orderList, pass: orderPass) => {
        if(pass?.scanned){
            return (
                <div className={`${style.paymentStatusBadge} ${style.paymentStatusBadgePaid}`}>
                    <VerifiedUserIcon className={`${style.accessGrantedIcon}`} />
                    Granted
                </div>
            );
        } else {
            return (
                <GlobalButton
                    className="borderedButtonStyle"
                    sx={{ width: "140px", fontSize: "13px !important" }}
                    buttonText="Grant Access"
                    onClick={(e) => {
                        e.stopPropagation();
                        grantAccessApi(order, pass);
                    }}
                    startIcon={<VerifiedUserIcon sx={{ fontSize: '15px !important' }} />}
                    disabled={checkDisableGrantAccess(pass, order)}
                />
            );
        }
    }

    const shouldShowSendQrCodeBtn = () => {
        if(selectedOrderDetail){
            return selectedOrderDetail?.order_items?.some((item: orderItem) => item?.passes?.length > 0);
        }
        return false;
    }

    return (
        <>
            <Grid container>
                <Grid item xs={12}>
                    <Grid container className={`${globalStyles.flexColumn} ${globalStyles.justifyCenter}`}>
                        <Grid item className={globalStyles.textCenter}>
                            <Typography className={contactStyle.popupTitleTypography}>
                                {`Order Details - Order #${selectedOrderDetail?.order_source_reference_id ?? ""}`}
                            </Typography>
                        </Grid>

                        <Divider className={contactStyle.popupTitleDivider} /> 

                        <Grid item className={globalStyles.fullwidth}>
                            <Grid container className={`${contactStyle.popupBodyGridContainer} ${style.orderDetailPopupBody}`} gap={2} sx={{ maxHeight: `${innerHeight}px !important`, overflow: 'auto' }}>
                                <Grid item xs={12}>
                                    <Grid container className={style.orderInfoCard} direction="column">
                                        <Grid container className={style.orderInfoTopSection}>
                                            {renderOrderInfoField("Buyer Name", getValueOrDefault(selectedOrderDetail?.buyer_name))}
                                            {renderOrderInfoField("Phone Number", getValueOrDefault(selectedOrderDetail?.phone_no))}
                                            {renderOrderInfoField("Reference ID", getValueOrDefault(selectedOrderDetail?.order_source_reference_id))}
                                            {renderOrderInfoField("Received from", getValueOrDefault(selectedOrderDetail?.whatsapp_phone_number))}
                                            {renderOrderInfoField("Order At",
                                                selectedOrderDetail?.order_created_timestamp
                                                    ? formatDateAccordingToTimezoneUsingMoment(selectedOrderDetail?.order_created_timestamp, userDetail.timezone_hour)
                                                    : "-"
                                            )}
                                            {renderOrderInfoField("Total Amount",
                                                selectedOrderDetail
                                                    ? `${selectedOrderDetail?.currency} ${thousandSeparator(String(Number(selectedOrderDetail?.total_amount)?.toFixed(2)))}`
                                                    : "-"
                                            )}
                                            {renderOrderInfoField("Paid At",
                                                selectedOrderDetail?.order_paid_timestamp
                                                    ? formatDateAccordingToTimezoneUsingMoment(selectedOrderDetail?.order_paid_timestamp, userDetail.timezone_hour)
                                                    : "-"
                                            )}
                                        </Grid>

                                        <Divider className={style.orderInfoDivider} />

                                        <Grid container className={style.orderInfoBottomSection}>
                                            {renderOrderInfoField("Payment Status",
                                                renderPaymentStatusBadge(selectedOrderDetail, formatString(selectedOrderDetail?.payment_status))
                                            )}
                                            {shouldShowSendQrCodeBtn() &&
                                                renderOrderInfoField("Send QR Code", renderSendQrCodeBtn(selectedOrderDetail))
                                            }
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid item xs={12}>
                                    <Grid container className={style.itemDetailsSection} direction="column" gap={1.5}>
                                        <Grid item xs={12}>
                                            <Typography component="span" className={style.itemDetailsTitle}>
                                                Item Details
                                            </Typography>
                                        </Grid>
                                        <TableContainer className={style.orderItemTableContainer}>
                                            <Table className={style.orderItemTable} size="small">
                                                <TableHead>
                                                    <TableRow className={style.orderItemTableHead}>
                                                        <TableCell>Item</TableCell>
                                                        <TableCell>Qty</TableCell>
                                                        <TableCell>Unit price</TableCell>
                                                        <TableCell>Amount</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody className={style.orderItemTableBody}>
                                                    {(selectedOrderDetail?.order_items && selectedOrderDetail?.order_items?.length > 0) ?
                                                        <>
                                                            {selectedOrderDetail?.order_items?.map((orderItem: orderItem, index: number) => {
                                                                const unitPrice = `${selectedOrderDetail?.currency} ${thousandSeparator(String((Number(orderItem?.unit_price ?? 0).toFixed(2))))}`;
                                                                const qty = orderItem?.quantity ?? 0;
                                                                const totalItemAmount = `${selectedOrderDetail?.currency} ${thousandSeparator(String(Number(orderItem?.line_amount_total ?? 0).toFixed(2)))}`;
                                                                
                                                                return (
                                                                    <TableRow key={orderItem?.order_item_id ?? index}>
                                                                        <TableCell>
                                                                            <Grid container alignItems={'center'} gap={1}>
                                                                                {orderItem?.image && 
                                                                                    <img src={orderItem?.image} alt="item_img" className={style.orderItemImage} onClick={() => window.open(orderItem?.image ?? '', '_blank')} />
                                                                                }
                                                                                <Grid item>
                                                                                    <Grid container direction={'column'}>
                                                                                        <Grid item className={style.itemName}>
                                                                                            {orderItem?.product_name}
                                                                                        </Grid>
                                                                                        <Grid item className={style.itemDetailDescription}>
                                                                                            {orderItem?.description}        
                                                                                        </Grid>
                                                                                    </Grid>
                                                                                </Grid>
                                                                            </Grid>
                                                                        </TableCell>
                                                                        <TableCell>{qty}</TableCell>
                                                                        <TableCell>{unitPrice}</TableCell>
                                                                        <TableCell>{totalItemAmount}</TableCell>
                                                                    </TableRow>
                                                                );
                                                            })}
                                                            <TableRow className={style.orderItemTotalRow}>
                                                                <TableCell>
                                                                    <span className={style.orderItemTotalLabel}>Total</span>
                                                                </TableCell>
                                                                <TableCell />
                                                                <TableCell />
                                                                <TableCell>
                                                                    <span className={style.orderItemTotalValue}>
                                                                        {selectedOrderDetail?.currency} {thousandSeparator(String(Number(selectedOrderDetail?.total_amount)?.toFixed(2)))}
                                                                    </span>
                                                                </TableCell>
                                                            </TableRow>
                                                        </>
                                                    :
                                                    <TableRow className={dashboardStyles.cellRow}>
                                                        <TableCell colSpan={4} className={style.orderItemEmptyCell}>
                                                            No Data Available
                                                        </TableCell>
                                                    </TableRow>
                                                }
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Grid>
                                </Grid>

                                {shouldShowSendQrCodeBtn() && 
                                    <Grid item xs={12}>
                                        <Grid container className={style.itemDetailsSection} direction="column" gap={1.5}>
                                            <Grid item xs={12}>
                                                <Typography component="span" className={style.itemDetailsTitle}>
                                                    Pass Details
                                                </Typography>
                                            </Grid>

                                            {selectedOrderDetail?.order_items?.filter((orderItem: orderItem) => orderItem?.passes?.length > 0)?.map((orderItem: orderItem, index: number) => (
                                                <Grid item xs={12} key={orderItem?.order_item_id ?? index} className={style.itemDetailCard}>
                                                    <Grid
                                                        container
                                                        className={style.itemDetailHeader}
                                                        alignItems="center"
                                                        justifyContent="space-between"
                                                        flexWrap="wrap"
                                                        gap={1.5}
                                                    >
                                                        <Grid item xs={12} sm className={style.itemDetailHeaderLeft}>
                                                            <Grid container alignItems="center" gap={1.25} wrap="nowrap">
                                                                {orderItem?.image && (
                                                                    <Grid item className={style.itemDetailImageGrid}>
                                                                        <img
                                                                            src={orderItem.image}
                                                                            alt="item_img"
                                                                            className={style.orderItemImage}
                                                                            onClick={() => window.open(orderItem.image ?? '', '_blank')}
                                                                        />
                                                                    </Grid>
                                                                )}
                                                                <Grid item>
                                                                    <Typography component="span" className={style.itemDetailName}>
                                                                        {orderItem?.product_name}
                                                                    </Typography>
                                                                </Grid>
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>

                                                    <Grid container className={style.itemPassesSection} direction="column">
                                                        <Grid item className={style.itemPassesHeader}>
                                                            <Grid container alignItems="center" gap={1} wrap="nowrap">
                                                                <Grid item>
                                                                    <GroupsOutlinedIcon fontSize="small" />
                                                                </Grid>
                                                                <Grid item>
                                                                    <Typography component="span" className={style.itemPassesHeaderText}>
                                                                        {orderItem?.passes?.length > 0
                                                                            ? `${orderItem.passes.length} Pass${orderItem.passes.length === 1 ? '' : 'es'}`
                                                                            : 'No passes for this item'}
                                                                    </Typography>
                                                                </Grid>
                                                            </Grid>
                                                        </Grid>

                                                        {orderItem?.passes?.map((pass: orderPass) => (
                                                            <Grid item key={pass?.qr_code_id} className={style.itemPassRow}>
                                                                <Grid
                                                                    container
                                                                    alignItems="center"
                                                                    justifyContent="space-between"
                                                                    flexWrap="wrap"
                                                                    gap={1}
                                                                >
                                                                    <Grid item xs={12} sm className={style.itemPassName} zeroMinWidth>
                                                                        <Grid container alignItems="center" gap={1} wrap="nowrap">
                                                                            {pass?.image && 
                                                                                <Grid item>
                                                                                    <img
                                                                                        src={pass.image}
                                                                                        alt="pass_img"
                                                                                        className={style.orderItemImage}
                                                                                        onClick={() => window.open(pass.image ?? '', '_blank')}
                                                                                    />
                                                                                </Grid>
                                                                            }
                                                                            <Grid item xs zeroMinWidth>
                                                                                <Typography component="span" className={style.itemPassNameText}>
                                                                                    {updatedPassName(pass, orderItem)}
                                                                                </Typography>
                                                                            </Grid>
                                                                        </Grid>
                                                                    </Grid>
                                                                    <Grid item xs={12} sm="auto" className={style.itemPassAction}>
                                                                        {grantAccessBtn(selectedOrderDetail, pass)}
                                                                    </Grid>
                                                                </Grid>
                                                            </Grid>
                                                        ))}
                                                    </Grid>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Grid>
                                }

                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </>
    )
}

export default OrderDetailPopup
